import * as THREE from "three";

export default class Raycaster {
  constructor(camera, scene, renderer) {
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;
    
    // Raycaster setup
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    
    // Intersection tracking
    this.INTERSECTED = null;
    this.intersectableObjects = [];
    
    // Event callbacks
    this.onClickCallback = null;
    this.onHoverCallback = null;
    this.onHoverOutCallback = null;
    
    // Configuration
    this.config = {
      enableHover: true,
      enableClick: true,
      hoverEmissiveColor: 0xff0000,
      recursive: false, // Search children objects
      objectsToCheck: null // If null, checks scene.children
    };
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Mouse move for hover effects
    this.renderer.domElement.addEventListener('mousemove', (event) => {
      this.onMouseMove(event);
    });
    
    // Mouse click for interactions
    this.renderer.domElement.addEventListener('click', (event) => {
      this.onClick(event);
    });
  }

  onMouseMove(event) {
    if (!this.config.enableHover) return;
    
    this.updateMousePosition(event);
    this.checkIntersections();
  }

  onClick(event) {
    if (!this.config.enableClick) return;
    
    this.updateMousePosition(event);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    
    const objectsToCheck = this.config.objectsToCheck || this.scene.children;
    const intersects = this.raycaster.intersectObjects(objectsToCheck, this.config.recursive);
    
    if (intersects.length > 0) {
      const clickedObject = intersects[0].object;
      console.log('Clicked object:', clickedObject);
      
      // Call custom click callback if provided
      if (this.onClickCallback) {
        this.onClickCallback(clickedObject, intersects[0]);
      }
      
      // Check for prompt property (from your example)
      if (clickedObject.prompt) {
        console.log('Object prompt:', clickedObject.prompt);
      }
    }
  }

  updateMousePosition(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  checkIntersections() {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    
    const objectsToCheck = this.config.objectsToCheck || this.scene.children;
    const intersects = this.raycaster.intersectObjects(objectsToCheck, this.config.recursive);
    
    if (intersects.length > 0) {
      const intersectedObject = intersects[0].object;
      
      // New object hovered
      if (this.INTERSECTED != intersectedObject) {
        // Reset previous object
        if (this.INTERSECTED) {
          this.resetObjectEmissive(this.INTERSECTED);
          
          // Call hover out callback
          if (this.onHoverOutCallback) {
            this.onHoverOutCallback(this.INTERSECTED);
          }
        }
        
        // Set new intersected object
        this.INTERSECTED = intersectedObject;
        this.setObjectEmissive(this.INTERSECTED, this.config.hoverEmissiveColor);
        
        // Call hover callback
        if (this.onHoverCallback) {
          this.onHoverCallback(this.INTERSECTED, intersects[0]);
        }
      }
    } else {
      // No intersection
      if (this.INTERSECTED) {
        this.resetObjectEmissive(this.INTERSECTED);
        
        // Call hover out callback
        if (this.onHoverOutCallback) {
          this.onHoverOutCallback(this.INTERSECTED);
        }
        
        this.INTERSECTED = null;
      }
    }
  }

  setObjectEmissive(object, color) {
    if (object.material && object.material.emissive) {
      // Store original emissive color if not already stored
      if (object.currentHex === undefined) {
        object.currentHex = object.material.emissive.getHex();
      }
      object.material.emissive.setHex(color);
    }
  }

  resetObjectEmissive(object) {
    if (object.material && object.material.emissive && object.currentHex !== undefined) {
      object.material.emissive.setHex(object.currentHex);
    }
  }

  // Configuration methods
  setConfig(newConfig) {
    Object.assign(this.config, newConfig);
  }

  setObjectsToCheck(objects) {
    this.config.objectsToCheck = objects;
  }

  enableHover(enable = true) {
    this.config.enableHover = enable;
  }

  enableClick(enable = true) {
    this.config.enableClick = enable;
  }

  setHoverColor(color) {
    this.config.hoverEmissiveColor = color;
  }

  // Event callback setters
  onHover(callback) {
    this.onHoverCallback = callback;
  }

  onHoverOut(callback) {
    this.onHoverOutCallback = callback;
  }

  onObjectClick(callback) {
    this.onClickCallback = callback;
  }

  // Add objects to intersectable list
  addIntersectableObject(object) {
    if (!this.intersectableObjects.includes(object)) {
      this.intersectableObjects.push(object);
      this.setObjectsToCheck(this.intersectableObjects);
    }
  }

  removeIntersectableObject(object) {
    const index = this.intersectableObjects.indexOf(object);
    if (index > -1) {
      this.intersectableObjects.splice(index, 1);
      this.setObjectsToCheck(this.intersectableObjects.length > 0 ? this.intersectableObjects : null);
    }
  }

  // Utility methods
  getIntersectedObject() {
    return this.INTERSECTED;
  }

  dispose() {
    this.renderer.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.renderer.domElement.removeEventListener('click', this.onClick);
  }
}
